import * as React from 'react';
import { useContext, useState, useMemo } from 'react';
import { DataGrid, GridRenderCellParams, GridSortCellParams, useGridApiRef } from '@mui/x-data-grid';
import { GridColDef, GridStateColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { FrostDates, Interval, Plant, PlantType, SeasonPlantingPlan } from '../Types.ts';
import { FrostDatesContext, SelectedPlantTypeContext } from '../AppContext.tsx';
import { Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, styled, useMediaQuery } from '@mui/material';
import { grey } from '@mui/material/colors';
import { ColdStratifyCell, CropCell, IndoorStartCell, OutdoorPlantingCell, PlantSpacingCell, StoreProductsCell } from './Cells.tsx';
import InfoIcon from '@mui/icons-material/InfoOutlined';




function dataGridColumnMapper(
  frostDates: FrostDates,
  plantRows: PlantType[],
  season: SeasonPlantingPlan,
) {
  if (!frostDates) return [];
  const cropRows =
    season === SeasonPlantingPlan.Spring
      ? plantRows.filter((plant) => plant.canPlantInSpring === true)
      : plantRows.filter((plant) => plant.canPlantInSummer === true);
  return cropRows.map((plant, i) => {
    return {
      rowId: plant.id,
      selected: false,
      index: 0,
      rowHeight: 0,
      containerWidth: 0,
      rowSelected: false,
      firstColumnToRender: 0,
      lastColumnToRender: 0,
      visibleColumns: [] as GridStateColDef[],
      renderedColumns: [] as GridStateColDef[],
      position: "left",
      focusedCell: null,
      tabbableCell: null,
      indoorStart: {
        matureDays: plant.daysToMature,
        indoorStartOffset: plant.indoorStartOffset,
      },
      outdoorPlanting: {
        springPlantingOffset: plant.springPlantingOffset,
        matureDays: plant.daysToMature,
      },
      ...plant,
      name: plant.name.replace(/^"(.*)"$/, "$1"),
      earliestIndoorOffset: plant.indoorStartOffset?.min ?? null,
      latestIndoorOffset: plant.indoorStartOffset?.max ?? null,
    };
  });
};

export type PlantRow = ReturnType<typeof dataGridColumnMapper>[0];

const ColumnHeader = styled(Typography)(({ theme }) => ({
    textTransform: 'uppercase',
    fontSize: '1.1em',
    letterSpacing: '2px',
    [theme.breakpoints.down('sm')]: {
        textWrap: 'wrap',
    },
    fontWeight: 'bold',
    marginRight: '5px',
    borderRadius: 0,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
}));
const HeaderInfoIcon = styled(InfoIcon)(({ theme }) => ({
    // [theme.breakpoints.down('sm')]: {
    //     display: 'none',
    // },
    color: theme.palette.primary.contrastText,
    marginTop: '-2px'
}));

function getColumnDefs(frostDates: FrostDates, plantingPlan: SeasonPlantingPlan, setSelectedPlantType: (plantType: PlantType) => void) {
    if (!frostDates) return [];
    const columnDefs: GridColDef[] = [
      {
        field: "name",
        headerName: "Crop",
        flex: 0.8,
        minWidth: 165,
        // maxWidth: 175,
        headerAlign: "left",
        hideable: false,
        renderHeader: () => <ColumnHeader>Crop</ColumnHeader>,
        renderCell: (
          params: GridRenderCellParams<PlantRow, string, string>
        ) => <CropCell crop={params.row.name} id={params.row.id} />,
      },
      {
        field: "indoorStart",
        type: "number",
        headerName: "Indoor Start",
        flex: 1,
        minWidth: 265,
        // maxWidth: 400,
        headerAlign: "left",
        align: "left",
        renderHeader: () => (
          <ColumnHeader>
            INDOOR START&nbsp;
            <Tooltip title="Crops without an indoor start date should be planted directly in the garden.">
              <HeaderInfoIcon fontSize="small" />
            </Tooltip>
          </ColumnHeader>
        ),
        renderCell: (params: GridRenderCellParams<PlantRow, null, null>) => (
          <IndoorStartCell
            plantingPlan={plantingPlan}
            frostDates={frostDates}
            row={params.row}
          />
        ),
        sortComparator: (
          _1,
          _2,
          cellParams1: GridSortCellParams<PlantRow["indoorStart"]>,
          cellParams2: GridSortCellParams<PlantRow["indoorStart"]>
        ) => {
          if (plantingPlan === SeasonPlantingPlan.Spring) {
            const cell1Min = cellParams1.value?.indoorStartOffset?.min ?? 999;
            const cell2Min = cellParams2.value?.indoorStartOffset?.min ?? 999;
            return cell1Min - cell2Min;
          } else {
            const maxMatureDays1 =
              0 - (cellParams1.value?.matureDays?.max ?? -999);
            const maxMatureDays2 =
              0 - (cellParams2.value?.matureDays?.max ?? -999);
            const minIndoorDays1 =
              cellParams1.value?.indoorStartOffset?.min ?? 999;
            const minIndoorDays2 =
              cellParams2.value?.indoorStartOffset?.min ?? 999;
            return (
              maxMatureDays1 +
              minIndoorDays1 -
              (maxMatureDays2 + minIndoorDays2)
            );
          }
        },
      },
      {
        field: "outdoorPlanting",
        type: "number",
        headerName: "Outdoor Planting",
        flex: 1,
        minWidth: 300,
        // maxWidth: 400,
        headerAlign: "left",
        align: "left",
        renderHeader: () => <ColumnHeader>Outdoor Planting</ColumnHeader>,
        renderCell: (params: GridRenderCellParams<PlantRow, null, null>) => (
          <OutdoorPlantingCell
            row={params.row}
            plantingPlan={plantingPlan}
            frostDates={frostDates}
          />
        ),
        sortComparator: (
          _1,
          _2,
          cellParams1: GridSortCellParams<PlantRow["outdoorPlanting"]>,
          cellParams2: GridSortCellParams<PlantRow["outdoorPlanting"]>
        ) => {
          if (plantingPlan === SeasonPlantingPlan.Spring) {
            const cell1Offset = cellParams1.value?.springPlantingOffset ?? 999;
            const cell2Offset = cellParams2.value?.springPlantingOffset ?? 999;
            return cell1Offset - cell2Offset;
          } else {
            const maxMatureDays1 =
              0 - (cellParams1.value?.matureDays?.max ?? -999);
            const maxMatureDays2 =
              0 - (cellParams2.value?.matureDays?.max ?? -999);
            return maxMatureDays1 - maxMatureDays2;
          }
        },
      },
      // {
      //     field: 'coldStratify',
      //     headerName: 'Cold Stratify',
      //     description: 'Exposing seeds to cold and moisture to mimic the winter conditions some seeds need for germination.',
      //     minWidth: 275,
      //     flex: 1,
      //     // maxWidth: 250,
      //     headerAlign: 'left',
      //     align: 'left',
      //     renderHeader: () => <ColumnHeader>
      //         Cold Stratify&nbsp;
      //         <Tooltip
      //             title='Exposing seeds to cold and moisture to mimic the winter conditions some seeds need for germination.'>
      //             <HeaderInfoIcon fontSize='small' />
      //         </Tooltip>
      //     </ColumnHeader>,
      //     renderCell: (params: GridRenderCellParams<PlantRow, null, null>) =>
      //         <ColdStratifyCell row={params.row} />,
      //     sortComparator: (_1, _2,
      //         cellParams1: GridSortCellParams<Interval | null>,
      //         cellParams2: GridSortCellParams<Interval | null>) => {
      //         const cell1Min = cellParams1.value?.min ?? 999;
      //         const cell2Min = cellParams2.value?.min ?? 999;
      //         return cell1Min - cell2Min;
      //     }
      // },
      {
        field: "plantSpacing",
        headerName: "Plant Spacing",
        flex: 1,
        minWidth: 275,
        // maxWidth: 400,
        headerAlign: "left",
        align: "left",
        renderHeader: () => <ColumnHeader>Plant Spacing</ColumnHeader>,
        renderCell: (params: GridRenderCellParams<PlantRow, null, null>) => (
          <PlantSpacingCell row={params.row} />
        ),
        sortComparator: (
          _1,
          _2,
          cellParams1: GridSortCellParams<Interval | null>,
          cellParams2: GridSortCellParams<Interval | null>
        ) => {
          const cell1Min = cellParams1.value?.min ?? 999;
          const cell2Min = cellParams2.value?.min ?? 999;
          return cell1Min - cell2Min;
        },
      },
      {
        field: "productIds",
        headerName: "Related Products",
        flex: 1,
        minWidth: 275,
        sortable: false,
        renderHeader: () => <ColumnHeader>Related Products</ColumnHeader>,
        renderCell: (params: GridRenderCellParams<PlantRow, null, null>) => {
          return <StoreProductsCell row={params.row} setSelectedPlantType={setSelectedPlantType} />;
        },
      },
      // {
      //     field: 'availableSgs',
      //     headerName: 'Available at SGS',
      // },
      // {
      //     field: 'availableSowright',
      //     headerName: 'Available at Sowright',
      // },
    ];
    return columnDefs;
}
const StyledGrid = styled(DataGrid)(({ theme }) => {
    const printing = useMediaQuery('print');
    return {
        border: `1px solid ${theme.palette.primary.main}`,
        [theme.breakpoints.down('sm')]: {
            border: 'none'
        },
        boxSizing: 'border-box',
        '& .MuiDataGrid-row': {
            boxSizing: 'border-box',
            '&:nth-of-type(even)': {
                backgroundColor: grey[100],
                // '& .MuiDataGrid-cell': {
                    
                // },
            },
            '& .MuiDataGrid-cell:nth-of-type(1)': {
                borderRight: `1px solid ${theme.palette.primary.light}`,
            },
            '&:hover': {
                backgroundColor: theme.palette.primary.light + ' !important',
            },
        },
        "& .MuiDataGrid-main": {
            // remove overflow hidden overwise sticky does not work
            overflow: "unset"
        },
        "& .MuiDataGrid-virtualScroller": {
            // remove the space left for the header
            marginTop: "0!important"
        },
        '& .MuiDataGrid-columnHeaders': {
            position: 'sticky',
            top: 0,
            zIndex: 1,
            textTransform: 'uppercase',
            fontSize: '1.1em',
            letterSpacing: '2px',
            borderRadius: 0,
            backgroundColor: printing ? 'white' : theme.palette.primary.main,
            color: printing ? 'black' : theme.palette.primary.contrastText,
            '& .MuiDataGrid-columnHeaderTitle': {
                color: printing ? 'black' : theme.palette.primary.contrastText,
                fontWeight: 'bold',
                lineHeight: '1em',
                textWrap: 'wrap',
            },

        },
        '& .MuiDataGrid-columnsContainer': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
        },
        '& .MuiDataGrid-menuIconButton': {
            color: 'rgba(255, 255, 255, 0.7)',
        },
        '& .MuiDataGrid-sortIcon': {
            color: 'rgba(255, 255, 255, 0.7)',
        },
    }
});
function PrintHeaderCell({ children }: { children: React.ReactNode }) {
    return <TableCell><Typography fontWeight='bold' fontSize='1.2em'>{children}</Typography></TableCell>
}
export function PlantingPlannerDataGrid({ season, rows  }: { season: SeasonPlantingPlan, rows: PlantType[]}) {
  const { frostDates } = useContext(FrostDatesContext);
  const {selectedPlantType, setSelectedPlantType} = useContext(SelectedPlantTypeContext);
    const [gridCols, plantRows] = useMemo(() => {
        if (!frostDates) return [[],[]];
        return [getColumnDefs(frostDates, season, setSelectedPlantType), dataGridColumnMapper(frostDates, rows, season)];
    }, [frostDates]);
    const [sortedRows, setSortedRows] = useState<PlantRow[]>([]);
    const apiRef = useGridApiRef();

    if (!frostDates) {
        return (<></>);
    } else {
        return (
            <>
                {(() => {
                    return (<Table sx={{
                        border: `1px solid black`,
                        breakAfter: 'auto',
                        breakBefore: 'avoid',
                        display: 'none',
                        '@media print': {
                            display: 'table',
                        }
                    }}>
                        <TableHead sx={{ breakInside: 'avoid' }}>
                            <TableRow sx={{ breakInside: 'avoid' }}>
                            <PrintHeaderCell>CROP</PrintHeaderCell>
                            <PrintHeaderCell>COLD STRATIFY</PrintHeaderCell>
                            <PrintHeaderCell>INDOOR START</PrintHeaderCell>
                            <PrintHeaderCell>OUTDOOR PLANTING</PrintHeaderCell>
                            <PrintHeaderCell>PLANT SPACING</PrintHeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedRows.map((row) => {
                                return (
                                    <TableRow sx={{breakInside: 'avoid'}} key={row.id}>
                                        <TableCell><Typography fontWeight='bold' fontSize='1.1em'>{row.name}</Typography></TableCell>
                                        <TableCell><ColdStratifyCell row={row} /></TableCell>
                                        <TableCell><IndoorStartCell plantingPlan={season} frostDates={frostDates} row={row} alignLeft={true} /></TableCell>
                                        <TableCell><OutdoorPlantingCell row={row} plantingPlan={season} frostDates={frostDates} /></TableCell>
                                        <TableCell><PlantSpacingCell row={row} /></TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>)
                })()}
                <StyledGrid
                    className='hideOnPrint'
                    onStateChange={() => {
                        setSortedRows((apiRef.current?.getSortedRows() ?? []) as PlantRow[]);
                    }}
                    apiRef={apiRef}
                    sx={{ width: '100%', borderRadius: 0 }}
                    rows={plantRows}
                    columns={gridCols}
                />
            </>
        );
    }
}